import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { Mail as MailIcon } from 'lucide-react';
import { useAppDispatch } from '@/store/hooks';
import { setSession, setError } from '@/store/authSlice';
import { authApi } from '@/api/auth';
import { friendlyError } from '@/lib/errorMessage';

type EmailStep = 'email' | 'code' | 'verifying';

export function EmailLogin() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<EmailStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setLocalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expiresInMinutes, setExpiresInMinutes] = useState<number | undefined>();
  const [remainingCodes, setRemainingCodes] = useState<number | undefined>();
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Countdown timer for cooldown
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownRef.current = setTimeout(() => {
        setCooldownSeconds((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) {
        clearTimeout(cooldownRef.current);
      }
    };
  }, [cooldownSeconds]);

  const sendCode = useCallback(
    async (options?: { validate?: boolean }) => {
      if (options?.validate) {
        if (!email.trim()) {
          setLocalError(t('login.email.enterEmail'));
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          setLocalError(t('login.email.invalidEmail'));
          return;
        }
      }

      setCode('');
      setIsLoading(true);
      setLocalError(null);

      try {
        const response = await authApi.requestEmailLoginCode(email);

        if (!response.success) {
          if (response.retryAfterSeconds) {
            setCooldownSeconds(response.retryAfterSeconds);
          }
          setLocalError(response.error || t('login.email.sendFailed'));
          return;
        }

        setExpiresInMinutes(response.expiresInMinutes);
        setRemainingCodes(response.remainingCodes);
        setCooldownSeconds(60);
        setStep('code');
      } catch (err) {
        setLocalError(friendlyError(err, t, 'login.email.sendFailed'));
      } finally {
        setIsLoading(false);
      }
    },
    [email, t]
  );

  const handleRequestCode = useCallback(() => sendCode({ validate: true }), [sendCode]);
  const handleResendCode = useCallback(() => sendCode(), [sendCode]);

  const handleVerifyCode = useCallback(async () => {
    if (!code.trim()) {
      setLocalError(t('login.email.enterCode'));
      return;
    }

    if (!/^[A-Z0-9]{6}$/i.test(code)) {
      setLocalError(t('login.email.invalidCode'));
      return;
    }

    setIsLoading(true);
    setStep('verifying');
    setLocalError(null);

    try {
      const response = await authApi.loginWithEmailCode(email, code);

      dispatch(
        setSession({
          sessionToken: response.sessionToken,
          user: response.user,
          expiresAt: response.expiresAt,
          loginMethod: 'email',
        })
      );
    } catch (err) {
      const msg = friendlyError(err, t, 'login.email.verifyFailed');
      setLocalError(msg);
      setStep('code');
      dispatch(setError(msg));
    } finally {
      setIsLoading(false);
    }
  }, [email, code, dispatch, t]);

  const handleBackToEmail = useCallback(() => {
    setStep('email');
    setCode('');
    setLocalError(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isLoading) {
        if (step === 'email') {
          void handleRequestCode();
        } else if (step === 'code') {
          void handleVerifyCode();
        }
      }
    },
    [step, isLoading, handleRequestCode, handleVerifyCode]
  );

  // Email step
  if (step === 'email') {
    return (
      <>
        <div className="login-method-divider" />
        <div className="status-container">
          <h2 className="status-title">{t('login.email.title')}</h2>
          <p className="status-description">{t('login.email.subtitle')}</p>

          {error && <div className="error-message">{error}</div>}

          <div className="email-input-container">
            <div className="input-with-icon">
              <MailIcon className="input-icon" />
              <input
                type="email"
                className="email-input"
                placeholder={t('login.email.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoFocus
              />
            </div>
          </div>

          {isLoading && <div className="spinner" />}

          <button
            className="btn-primary"
            onClick={handleRequestCode}
            disabled={isLoading || !email.trim()}
          >
            {t('login.email.sendCode')}
          </button>
        </div>
      </>
    );
  }

  // Verifying step - centered spinner
  if (step === 'verifying') {
    return (
      <>
        <div className="login-method-divider" />
        <div className="status-container verifying-container">
          <div className="spinner" />
          <h2 className="status-title">{t('login.email.verifying')}</h2>
          <p className="status-description">{t('login.email.verifyingDesc')}</p>
        </div>
      </>
    );
  }

  // Code verification step
  return (
    <>
      <div className="login-method-divider" />
      <div className="status-container">
        <h2 className="status-title">{t('login.email.codeTitle')}</h2>
        <p className="status-description">
          {expiresInMinutes
            ? t('login.email.codeSentExpiry', { email, minutes: expiresInMinutes })
            : t('login.email.codeSent', { email })}
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="code-input-container">
          <Input.OTP
            length={6}
            value={code}
            onChange={(val) => {
              setCode(val.toUpperCase());
            }}
            disabled={isLoading}
            autoFocus
            formatter={(val) => val.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}
            size="large"
          />
        </div>

        {isLoading && <div className="spinner" />}

        <button
          className="btn-primary"
          onClick={handleVerifyCode}
          disabled={isLoading || code.length !== 6}
        >
          {t('login.email.verify')}
        </button>

        <div className="resend-section">
          <button
            className="btn-link"
            onClick={handleResendCode}
            disabled={isLoading || cooldownSeconds > 0}
          >
            {cooldownSeconds > 0
              ? t('login.email.resendIn', { seconds: cooldownSeconds })
              : t('login.email.resendCode')}
          </button>
          <span className="divider">|</span>
          <button className="btn-link" onClick={handleBackToEmail} disabled={isLoading}>
            {t('login.email.changeEmail')}
          </button>
        </div>
        {remainingCodes !== undefined && remainingCodes <= 1 && (
          <p className="remaining-codes-warning">
            {remainingCodes === 0
              ? t('login.email.rateLimited')
              : t('login.email.codesRemaining', { count: remainingCodes })}
          </p>
        )}
      </div>
    </>
  );
}

export default EmailLogin;
